// context/AuthContext.tsx
import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode, JSX } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  User as FirebaseUser,
  UserCredential,
} from 'firebase/auth';
import { ref, set, update, get, onValue, Unsubscribe } from 'firebase/database';
import { auth, db } from '../config/firebase';
import { getCallable } from '../services/firebaseFunctions';
import { ACCOUNT_TYPES } from '../config/constants';
import { logger } from '../utils/logger';
import type { AccountType } from '../types/roles.types';

// Extended account type including parent/child for legacy compatibility
type ExtendedAccountType = AccountType | 'parent' | 'child';

// Types
export interface UserData {
  uid: string;
  email: string | null;
  displayName?: string | null;
  firstName?: string;
  lastName?: string | null;
  /** Cached display name for store accounts. Canonical source is
   *  /stores/{storeId}/name; this denorm lets dropdowns + dash-
   *  boards + topbar render the store's name instead of the
   *  owner's email. Maintained by the invite-claim flow, the
   *  Settings save path, and a lazy backfill on auth. */
  storeName?: string;
  /** Per-user theme preference. 'classic' (default) keeps the
   *  shipping UI; 'preview' opts into the dashboard redesign
   *  (rolled out role by role); 'minimal' is the retired first
   *  redesign attempt and is treated as classic. Stored on
   *  /users/{uid}/uiTheme so the toggle persists across devices. */
  uiTheme?: 'classic' | 'minimal' | 'preview';
  // Legacy role field - being phased out in favor of per-tournament roles
  role?: string;
  userType?: 'staff' | 'player';
  // New account type system (admin, store, staff, user) + legacy parent/child
  accountType?: ExtendedAccountType;
  approved?: boolean;
  inactive?: boolean;
  visitorId?: string;
  bandaiMemberId?: string;
  languages?: string[];
  tournamentAssignments?: Record<string, boolean>;
  deckCheckTeamAssignments?: Record<string, boolean>;
  featureMatchTeamAssignments?: Record<string, boolean>;
  headJudgeTeamAssignments?: Record<string, boolean>;
  floorTeamAssignments?: Record<string, string>;
  onBreak?: boolean;
  childAccounts?: string[];
  parentUid?: string;
  createdAt?: number;
  [key: string]: unknown;
}

export interface AuthContextValue {
  user: UserData | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<FirebaseUser>;
  register: (
    email: string,
    password: string,
    firstName: string,
    languages?: string[],
    lastName?: string,
  ) => Promise<FirebaseUser>;
  registerPlayer: (email: string, password: string, visitorId: string, displayName?: string, bandaiMemberId?: string) => Promise<FirebaseUser>;
  registerParent: (email: string, password: string, name: string, visitorId?: string | null, bandaiMemberId?: string | null) => Promise<FirebaseUser>;
  loginAsChild: (username: string, password: string) => Promise<FirebaseUser>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<boolean>;
  hasRole: (requiredRoles: string | string[]) => boolean;
  isManager: () => boolean;
  isAdmin: () => boolean;
  isStoreOwner: () => boolean;
  isPlayer: () => boolean;
  isStaff: () => boolean;
  isParent: () => boolean;
  isChild: () => boolean;
  hasChildren: () => boolean;
  clearError: () => void;
}

export interface AuthProviderProps {
  children: ReactNode;
}

// Create the context
const AuthContext = createContext<AuthContextValue | null>(null);

// Per-process memo so multiple auth state changes on the same
// session don't fire repeated backfills.
const backfilledStoreNames = new Set<string>();

/** Exported for unit testing. Caller is responsible for the
 *  precondition checks (accountType === 'store', no existing
 *  storeName, storeId present). */
export async function backfillStoreName(uid: string, storeId: string): Promise<void> {
  if (backfilledStoreNames.has(uid)) return;
  backfilledStoreNames.add(uid);
  try {
    const nameSnap = await get(ref(db, `stores/${storeId}/name`));
    const storeName = nameSnap.val() as string | null;
    if (!storeName) return;
    await update(ref(db, `users/${uid}`), { storeName });
  } catch {
    // Best-effort. A failure here just means the user record
    // stays without a cached storeName and the display sites
    // continue to fall back to firstName / email until the next
    // login. Don't let it crash auth.
    backfilledStoreNames.delete(uid);
  }
}

/**
 * AuthProvider - Provides authentication state and methods to the app
 */
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isRegistering = useRef(false);

  // Listen for auth state changes
  useEffect(() => {
    let userDataUnsubscribe: Unsubscribe | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      // Clean up previous user data listener
      if (userDataUnsubscribe) {
        userDataUnsubscribe();
        userDataUnsubscribe = null;
      }

      // Skip processing during registration - the register functions handle
      // the database write before this listener should react
      if (isRegistering.current) {
        return;
      }

      try {
        if (firebaseUser) {
          // Subscribe to real-time updates for user data
          const userRef = ref(db, `users/${firebaseUser.uid}`);

          userDataUnsubscribe = onValue(userRef, (snapshot) => {
            const userData = snapshot.val() as Record<string, unknown> | null;

            // Check if this is a player account (auto-approved)
            const isPlayerAccount = userData?.userType === 'player';

            // Check if account is inactive (deactivated by admin)
            if (userData?.inactive) {
              setUser(null);
              setLoading(false);
              setError('Your account has been deactivated. Please contact an administrator.');
              signOut(auth);
              return;
            }

            if (userData && (userData.approved || isPlayerAccount)) {
              // Merge Firebase user with database data
              // Include all userData fields (tournamentAssignments, deckCheckTeamAssignments, onBreak, etc.)
              const enrichedUser: UserData = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
                ...userData,
                // Check for admin email override
                ...(firebaseUser.email === 'admin@example.com' && { role: 'admin' }),
              };
              setUser(enrichedUser);
              setLoading(false);

              // Lazy backfill of storeName for pre-existing store
              // accounts that don't have it on their user record
              // yet. Canonical source is /stores/{storeId}/name;
              // we cache it on the user so dropdowns / topbar
              // don't have to join. Best-effort, fire-and-forget.
              if (
                enrichedUser.accountType === 'store' &&
                !enrichedUser.storeName &&
                typeof enrichedUser.storeId === 'string' &&
                enrichedUser.storeId
              ) {
                backfillStoreName(firebaseUser.uid, enrichedUser.storeId);
              }
            } else {
              // User not approved (staff account pending)
              setUser(null);
              setLoading(false);
              if (userData && !userData.approved && !isPlayerAccount) {
                setError('Your account is pending approval.');
                signOut(auth);
              }
            }
          }, (err) => {
            logger.error('User data error:', err);
            setError(err.message);
            setLoading(false);
          });
        } else {
          setUser(null);
          setLoading(false);
        }
      } catch (err) {
        logger.error('Auth state error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (userDataUnsubscribe) {
        userDataUnsubscribe();
      }
    };
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string): Promise<FirebaseUser> => {
    setError(null);
    try {
      const result: UserCredential = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Register a new staff user
   * New staff get accountType: 'user' until approved, then 'staff'
   */
  const register = useCallback(async (
    email: string,
    password: string,
    firstName: string,
    languages: string[] = [],
    lastName = '',
  ): Promise<FirebaseUser> => {
    setError(null);
    isRegistering.current = true;
    try {
      const result: UserCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Store additional user data. New staff are AUTO-APPROVED — they get
      // accountType 'staff' + approved:true immediately (no manual approval
      // step), but no tournament role until an admin/Manager assigns them.
      // lastName lands alongside firstName so displays can render
      // 'Firstname L.' via obscureName instead of bare first names.
      await set(ref(db, `users/${result.user.uid}`), {
        firstName,
        lastName: lastName.trim() || null,
        email,
        languages,
        approved: true,
        accountType: 'staff',
        role: 'pending',      // Legacy: kept for backwards compatibility
        userType: 'staff',
        approvedAt: Date.now(),
        createdAt: Date.now(),
      });

      // Auto-approved — keep them signed in so they can use the app right away.
      return result.user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      isRegistering.current = false;
    }
  }, []);

  /**
   * Register a new player account
   * Players are auto-approved and tied to their visitor ID
   */
  const registerPlayer = useCallback(async (email: string, password: string, visitorId: string, displayName = '', bandaiMemberId?: string): Promise<FirebaseUser> => {
    setError(null);
    isRegistering.current = true;
    try {
      const trimmedId = visitorId.trim();
      const trimmedBandaiId = bandaiMemberId?.trim() || null;

      // Check if this Player ID is already taken (via Cloud Function, works without auth)
      if (trimmedId) {
        const checkPlayerId = await getCallable<{ playerId: string }, { available: boolean; reason: string | null }>('checkPlayerIdAvailable');
        const checkResult = await checkPlayerId({ playerId: trimmedId });
        if (!checkResult.data.available) {
          throw new Error(checkResult.data.reason || 'This Player ID is already registered to another account.');
        }
      }

      const result: UserCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Store player user data - auto-approved
      const userData: Record<string, unknown> = {
        email,
        visitorId: trimmedId || null,
        displayName: displayName.trim() || email.split('@')[0],
        approved: true,           // Players are auto-approved
        accountType: 'player',    // Player account type
        role: 'player',           // Legacy: kept for backwards compatibility
        userType: 'player',
        createdAt: Date.now(),
      };

      if (trimmedBandaiId) {
        userData.bandaiMemberId = trimmedBandaiId;
      }

      await set(ref(db, `users/${result.user.uid}`), userData);

      // Register the visitor ID in the global index
      if (trimmedId) {
        await set(ref(db, `visitorIds/${trimmedId}`), result.user.uid);
      }
      // Mirror the Bandai id into its own reverse index so the
      // matchmaking lookup can resolve it for tcgOwner === 'bandai'
      // tournaments.
      if (trimmedBandaiId) {
        await set(ref(db, `bandaiMemberIds/${trimmedBandaiId}`), result.user.uid);
      }

      // Don't sign out - players are auto-approved
      return result.user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      isRegistering.current = false;
    }
  }, []);

  /**
   * Register a new parent account
   * Parents are auto-approved and can manage child accounts
   */
  const registerParent = useCallback(async (email: string, password: string, name: string, visitorId: string | null = null, bandaiMemberId: string | null = null): Promise<FirebaseUser> => {
    setError(null);
    isRegistering.current = true;
    try {
      const trimmedId = visitorId?.trim() || null;
      const trimmedBandaiId = bandaiMemberId?.trim() || null;

      // Check uniqueness if a player ID is provided (via Cloud Function, works without auth)
      if (trimmedId) {
        const checkPlayerId = await getCallable<{ playerId: string }, { available: boolean; reason: string | null }>('checkPlayerIdAvailable');
        const checkResult = await checkPlayerId({ playerId: trimmedId });
        if (!checkResult.data.available) {
          throw new Error(checkResult.data.reason || 'This Player ID is already registered to another account.');
        }
      }

      const result: UserCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Store parent user data - auto-approved
      const userData: Record<string, unknown> = {
        email,
        name: name.trim(),
        displayName: name.trim(),
        visitorId: trimmedId, // Optional - only if parent also plays
        approved: true,
        role: 'player',
        userType: 'player',
        accountType: ACCOUNT_TYPES.PARENT,
        childAccounts: [],
        createdAt: Date.now(),
      };

      if (trimmedBandaiId) {
        userData.bandaiMemberId = trimmedBandaiId;
      }

      await set(ref(db, `users/${result.user.uid}`), userData);

      // Register the visitor ID in the global index if provided
      if (trimmedId) {
        await set(ref(db, `visitorIds/${trimmedId}`), result.user.uid);
      }
      // Same for the Bandai reverse index so the matchmaking
      // lookup can resolve a Bandai-only player against the
      // tournament's tcgOwner === 'bandai' tournaments.
      if (trimmedBandaiId) {
        await set(ref(db, `bandaiMemberIds/${trimmedBandaiId}`), result.user.uid);
      }

      // Don't sign out - parents are auto-approved
      return result.user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      isRegistering.current = false;
    }
  }, []);

  /**
   * Login as a child account using username
   */
  const loginAsChild = useCallback(async (username: string, password: string): Promise<FirebaseUser> => {
    setError(null);
    try {
      // Get the generated email for this username via Cloud Function
      const getLoginEmail = await getCallable<{ username: string }, { email: string }>('getChildLoginEmail');
      const { email } = (await getLoginEmail({ username })).data;

      // Sign in with the generated email
      const result: UserCredential = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      // Make error message user-friendly
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code === 'functions/not-found' || firebaseError.message?.includes('Invalid username')) {
        setError('Invalid username or password');
      } else {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
      throw err;
    }
  }, []);

  /**
   * Logout current user
   */
  const logout = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      await signOut(auth);
      setUser(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(async (email: string): Promise<boolean> => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Check if user has required legacy role (for backwards compatibility)
   * Prefer using isAdmin, isStaff, isStoreOwner for new code
   */
  const hasRole = useCallback((requiredRoles: string | string[]): boolean => {
    if (!user) return false;
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return user.role ? roles.includes(user.role) : false;
  }, [user]);

  /**
   * Check if user is admin or manager
   * Uses new accountType with legacy fallback
   */
  const isManager = useCallback((): boolean => {
    if (!user) return false;
    // New system: check accountType
    if (user.accountType === 'admin') return true;
    // Legacy fallback: check role
    return user.role ? ['admin', 'Manager'].includes(user.role) : false;
  }, [user]);

  /**
   * Check if user is admin
   * Uses new accountType with legacy fallback
   */
  const isAdmin = useCallback((): boolean => {
    if (!user) return false;
    // New system: check accountType
    if (user.accountType === 'admin') return true;
    // Legacy fallback: check role
    return user.role === 'admin';
  }, [user]);

  /**
   * Check if user is a store owner
   */
  const isStoreOwner = useCallback((): boolean => {
    if (!user) return false;
    return user.accountType === 'store';
  }, [user]);

  /**
   * Check if user is a player (not staff).
   *
   * accountType (new system) is the source of truth: a user that has
   * been promoted to staff/admin (or has a parent/store accountType)
   * is NOT a player, even if the legacy userType: 'player' field is
   * still on the doc. Three promotion paths only flip accountType
   * and leave userType alone (UserApprovalPage, authService,
   * parentChild cloud function), so without this precedence the
   * legacy field would keep a promoted user stuck on the player side
   * of routing.
   */
  const isPlayer = useCallback((): boolean => {
    if (!user) return false;
    if (
      user.accountType === 'staff' ||
      user.accountType === 'admin' ||
      user.accountType === 'store' ||
      user.accountType === 'parent'
    ) {
      return false;
    }
    return (
      user.userType === 'player' ||
      user.accountType === 'player' ||
      user.accountType === 'user'
    );
  }, [user]);

  /**
   * Check if user is staff (can be assigned to tournaments)
   * Uses new accountType with legacy fallback
   */
  const isStaff = useCallback((): boolean => {
    if (!user) return false;
    // New system: check accountType
    if (user.accountType === 'staff' || user.accountType === 'admin') return true;
    // Legacy fallback: check userType
    return user.userType === 'staff' || (user.userType !== 'player' && user.approved === true);
  }, [user]);

  /**
   * Check if user is a parent account
   */
  const isParent = useCallback((): boolean => user?.accountType === 'parent', [user]);

  /**
   * Check if user is a child account
   */
  const isChild = useCallback((): boolean => user?.accountType === 'child', [user]);

  /**
   * Check if user has any children
   */
  const hasChildren = useCallback((): boolean => (user?.childAccounts?.length ?? 0) > 0, [user]);

  const clearError = useCallback(() => setError(null), []);

  // Memoising the context value stops every consumer of useAuth()
  // from re-rendering on every Firebase user-record push (which
  // happens often — onBreak toggles, assignment changes, etc).
  // Without this, useNavPermissions / useNavSubscriptions recompute
  // on each push because their `user` dep changes identity even
  // when no relevant field did. All callbacks are memoised above
  // so their identities only flip when their own deps change.
  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    error,
    login,
    register,
    registerPlayer,
    registerParent,
    loginAsChild,
    logout,
    resetPassword,
    hasRole,
    isManager,
    isAdmin,
    isStoreOwner,
    isPlayer,
    isStaff,
    isParent,
    isChild,
    hasChildren,
    clearError,
  }), [
    user, loading, error,
    login, register, registerPlayer, registerParent, loginAsChild, logout, resetPassword,
    hasRole, isManager, isAdmin, isStoreOwner, isPlayer, isStaff, isParent, isChild, hasChildren,
    clearError,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth hook - Access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
